import React, {
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Outlet } from "react-router-dom";
import {
  PRODUCT,
  hasProductAccess,
  readMembershipsForProduct,
  readSuperUser,
  reconcileActiveOrg,
} from "../utils/activeOrg";
import { isOrgRefused, subscribeOrgRefusal } from "../utils/orgRefusal";
import NotAuthorized from "../views/NotAuthorized";
import { RealtimeProvider } from "../providers/RealtimeProvider";
import FullPageSpin from "./FullPageSpin";

// Both marks live in sessionStorage because what they guard against spans a full
// page navigation - a trip to Auth0, a reload - and anything held in memory is
// gone by the time we could ask it. Guarded like auth0-instance's reauth mark:
// sessionStorage throws outright in some privacy modes, and losing a mark costs
// the loop guard, not correctness.
//
// LOGIN: when AuthLayout last sent the user to Auth0 to sign in.
// RECHECK: when AuthLayout last reloaded on a fresh token after the backend
// refused the old one.
const LOGIN_MARK_KEY = `vectech.${PRODUCT}.loginAt`;
const RECHECK_MARK_KEY = `vectech.${PRODUCT}.orgRecheckAt`;
const RETRY_WINDOW_MS = 60_000;

const markIsRecent = (key: string): boolean => {
  try {
    const at = Number(window.sessionStorage.getItem(key)) || 0;
    return !!at && Date.now() - at < RETRY_WINDOW_MS;
  } catch (e) {
    return false;
  }
};

const writeMark = (key: string, on: boolean): void => {
  try {
    if (on) window.sessionStorage.setItem(key, String(Date.now()));
    else window.sessionStorage.removeItem(key);
  } catch (e) {
    // Ignored - see above.
  }
};

// The codes Auth0 returns on the callback when the tenant itself refused the
// login (an Action's api.access.deny, a blocked user). Sending the user straight
// back would only meet the same refusal: the Auth0 session is still there, so it
// never even reaches a login form. That round trip, rendered as nothing, was a
// white page that reloaded forever.
const REFUSED_LOGIN_CODES = new Set(["access_denied", "unauthorized"]);

const currentPath = () => window.location.pathname + window.location.search;

// Why the app is not mounted: the token carries no organization for Scout, or it
// did but the backend refused it anyway.
type GateReason = "no-org" | "refused";

type Gate = { reason: GateReason; status: "checking" | "denied" };

export const AuthLayout = () => {
  const {
    isLoading,
    isAuthenticated,
    error,
    user,
    loginWithRedirect,
    getAccessTokenSilently,
    getIdTokenClaims,
    logout,
  } = useAuth0();
  const refused = useSyncExternalStore(subscribeOrgRefusal, isOrgRefused);
  const [gate, setGate] = useState<Gate | undefined>();
  const [retrying, setRetrying] = useState(false);
  const [signInFailed, setSignInFailed] = useState(false);

  // Reconciled here, before <Outlet /> renders anything that fetches. The
  // request layer reads the stored organization at call time, so a stale value
  // would go out on the very first call and come back 403 - including on the
  // endpoint that lists the valid organizations, leaving no way back. Reading
  // memberships off the token rather than from an API call is what avoids that
  // deadlock.
  //
  // Deliberately during render rather than in an effect: a parent's effect runs
  // AFTER its children have rendered and fired their own effects, so an effect
  // here would land after the first requests had already gone out with the stale
  // value. It is idempotent, so React's double-render in development is
  // harmless.
  if (isAuthenticated) {
    reconcileActiveOrg(readMembershipsForProduct(user), {
      superUser: readSuperUser(user),
    });
  }

  // After reconcile, which a superuser's access depends on.
  const reason: GateReason | undefined = !isAuthenticated
    ? undefined
    : refused
      ? "refused"
      : hasProductAccess(user)
        ? undefined
        : "no-org";

  const signOut = useCallback(
    () => logout({ logoutParams: { returnTo: window.location.origin } }),
    [logout],
  );

  /**
   * Mint a fresh token and see whether it grants Scout.
   *
   * The token in the cache can predate the user's membership - it is kept in
   * localstorage and renewed from the same refresh token for weeks. cacheMode
   * "off" goes to Auth0 with the refresh token instead of answering from that
   * cache, which runs the tenant's login pipeline again and so picks up whatever
   * membership has been granted since. auth0-react updates `user` from the new ID
   * token before this resolves, and the request layer's own Auth0 client reads
   * the same cache, so both see it.
   *
   * No refresh token, or Auth0 will not honour it: log out, so the next sign-in
   * starts clean. A fresh token that still grants nothing: the user is simply not
   * in an organization yet.
   */
  const recheck = useCallback(
    async (why: GateReason, { manual = false } = {}) => {
      if (manual) setRetrying(true);
      else setGate({ reason: why, status: "checking" });

      try {
        await getAccessTokenSilently({ cacheMode: "off" });
      } catch (err) {
        signOut();
        return;
      }

      const claims = await getIdTokenClaims();
      if (!hasProductAccess(claims)) {
        setRetrying(false);
        setGate({ reason: why, status: "denied" });
        return;
      }

      if (why === "refused") {
        // Every cache in the app is full of refusals by now, and none of them
        // resets on its own - the same reasoning as OrgSelector's switch. The
        // mark is what stops a backend that refuses this token too from turning
        // this into a reload loop.
        writeMark(RECHECK_MARK_KEY, true);
        window.location.reload();
        return;
      }

      // "no-org": the app has not mounted yet, so there is nothing stale to
      // clear. `user` now grants access, `reason` clears, and <Outlet /> renders.
      setRetrying(false);
    },
    [getAccessTokenSilently, getIdTokenClaims, signOut],
  );

  useEffect(() => {
    if (!reason || gate?.reason === reason) return;
    if (reason === "refused" && markIsRecent(RECHECK_MARK_KEY)) {
      // We reloaded on a fresh token moments ago and the backend still refuses.
      setGate({ reason, status: "denied" });
      return;
    }
    recheck(reason);
  }, [reason, gate, recheck]);

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      // Signed in: a sign-in failure from here on is a new one.
      writeMark(LOGIN_MARK_KEY, false);
      return;
    }
    if (signInFailed) return;

    if (error) {
      // The URL still carries the callback that failed. Left there, a reload
      // would replay the same failure instead of starting over.
      window.history.replaceState({}, document.title, window.location.pathname);
      console.warn("Auth0 sign-in failed", error);
      const code = (error as { error?: string }).error;
      // A refusal from the tenant, or a failure straight after our own
      // redirect: another trip to Auth0 would only come back the same way.
      // Anything else - a callback visited again with Back, a transaction lost
      // with the tab - is worth one fresh attempt.
      if (
        (code && REFUSED_LOGIN_CODES.has(code)) ||
        markIsRecent(LOGIN_MARK_KEY)
      ) {
        setSignInFailed(true);
        return;
      }
    }

    writeMark(LOGIN_MARK_KEY, true);
    loginWithRedirect({ appState: { returnTo: currentPath() } }).catch(() =>
      setSignInFailed(true),
    );
  }, [isLoading, isAuthenticated, error, signInFailed, loginWithRedirect]);

  if (isAuthenticated) {
    if (!reason) {
      // Inside the gate, so a socket is only ever opened for a token the app
      // itself would mount for, and is closed again the moment it would not.
      return (
        <RealtimeProvider>
          <Outlet />
        </RealtimeProvider>
      );
    }
    if (gate?.reason === reason && gate.status === "denied") {
      return (
        // A refused token may well name an organization - one deactivated, or
        // a membership revoked since - so it is not told it has none.
        <NotAuthorized
          reason={reason === "refused" ? "denied" : "no-org"}
          email={user?.email}
          retrying={retrying}
          onRetry={() => recheck(reason, { manual: true })}
          onLogout={signOut}
        />
      );
    }
    return <FullPageSpin />;
  }

  if (signInFailed) {
    const code = (error as { error?: string } | undefined)?.error;
    return (
      <NotAuthorized
        reason={
          code && REFUSED_LOGIN_CODES.has(code) ? "denied" : "sign-in-failed"
        }
        detail={error?.message}
        retrying={retrying}
        onRetry={() => {
          setRetrying(true);
          writeMark(LOGIN_MARK_KEY, true);
          loginWithRedirect({ appState: { returnTo: currentPath() } }).catch(
            () => setRetrying(false),
          );
        }}
        onLogout={signOut}
      />
    );
  }

  // Loading, or on the way to Auth0.
  return <FullPageSpin />;
};
